export const AUTH_STATUSES = [
  "anonymous-local",
  "starting",
  "callback-verifying",
  "authenticated-no-organization",
  "authenticated-no-project",
  "project-readonly",
  "expired",
  "refused",
  "unavailable"
] as const;

export type AuthStatus = (typeof AUTH_STATUSES)[number];
export type AuthToolId = "inspect" | "codes" | "components";

export interface AuthReturnTarget {
  readonly route: "/workspace";
  readonly tool: AuthToolId;
}

export interface AuthAttempt {
  readonly id: string;
  readonly state: string;
  readonly nonce: string;
  readonly pkceBinding: string;
}

export interface AuthCallback {
  readonly id: string;
  readonly attemptId: string;
  readonly state: string;
  readonly nonce: string;
  readonly pkceBinding: string;
}

export interface AuthProject {
  readonly id: string;
  readonly name: string;
  readonly readOnly: true;
}

export interface AuthOrganization {
  readonly id: string;
  readonly name: string;
  readonly projects: readonly AuthProject[];
}

export interface AuthIdentity {
  readonly subject: string;
  readonly displayName: string;
  readonly email: string;
}

export interface AuthState {
  readonly status: AuthStatus;
  readonly returnTo: AuthReturnTarget | null;
  readonly attempt: AuthAttempt | null;
  readonly activeCallbackId: string | null;
  readonly consumedCallbackIds: readonly string[];
  readonly identity: AuthIdentity | null;
  readonly organizations: readonly AuthOrganization[];
  readonly organization: AuthOrganization | null;
  readonly project: AuthProject | null;
  readonly message: string;
}

export const initialAuthState: AuthState = {
  status: "anonymous-local",
  returnTo: null,
  attempt: null,
  activeCallbackId: null,
  consumedCallbackIds: [],
  identity: null,
  organizations: [],
  organization: null,
  project: null,
  message: "Local Open, Inspect, and Export remain available without signing in."
};

export type AuthEvent =
  | { readonly type: "start"; readonly attempt: AuthAttempt; readonly returnTo: AuthReturnTarget }
  | { readonly type: "callback-received"; readonly callback: AuthCallback }
  | {
    readonly type: "callback-verified";
    readonly callbackId: string;
    readonly identity: AuthIdentity;
    readonly organizations: readonly AuthOrganization[];
  }
  | { readonly type: "select-organization"; readonly organizationId: string }
  | { readonly type: "select-project"; readonly projectId: string }
  | { readonly type: "expired"; readonly message: string }
  | { readonly type: "refused"; readonly message: string; readonly callbackId?: string }
  | { readonly type: "unavailable"; readonly message: string }
  | { readonly type: "cancel" }
  | { readonly type: "sign-out" }
  | {
    readonly type: "session-restored";
    readonly identity: AuthIdentity;
    readonly organizations: readonly AuthOrganization[];
    readonly returnTo: AuthReturnTarget;
  };

function clearCloudContext(
  state: AuthState,
  status: AuthStatus,
  message: string,
  consumedCallbackIds = state.consumedCallbackIds
): AuthState {
  return {
    ...state,
    status,
    attempt: null,
    activeCallbackId: null,
    consumedCallbackIds,
    identity: null,
    organizations: [],
    organization: null,
    project: null,
    message
  };
}

function consumeCallback(state: AuthState, callbackId: string): readonly string[] {
  return state.consumedCallbackIds.includes(callbackId)
    ? state.consumedCallbackIds
    : [...state.consumedCallbackIds, callbackId];
}

export function reduceAuthState(state: AuthState, event: AuthEvent): AuthState {
  switch (event.type) {
    case "session-restored":
      return {
        ...clearCloudContext(state, "authenticated-no-organization", "Choose an organization to continue."),
        returnTo: event.returnTo,
        identity: event.identity,
        organizations: event.organizations
      };
    case "start":
      return {
        ...clearCloudContext(state, "starting", "Preparing a secure sign-in handoff."),
        returnTo: event.returnTo,
        attempt: event.attempt
      };
    case "callback-received": {
      const callback = event.callback;
      const consumed = consumeCallback(state, callback.id);
      if (state.consumedCallbackIds.includes(callback.id)) {
        return clearCloudContext(state, "refused", "This sign-in callback was already used.", consumed);
      }
      if (
        state.status !== "starting" ||
        state.attempt === null ||
        callback.attemptId !== state.attempt.id ||
        callback.state !== state.attempt.state ||
        callback.nonce !== state.attempt.nonce ||
        callback.pkceBinding !== state.attempt.pkceBinding
      ) {
        return clearCloudContext(state, "refused", "Sign-in state, nonce, or PKCE verification failed.", consumed);
      }
      return {
        ...state,
        status: "callback-verifying",
        activeCallbackId: callback.id,
        message: "Verifying the sign-in callback."
      };
    }
    case "callback-verified":
      if (state.status !== "callback-verifying" || state.activeCallbackId !== event.callbackId) {
        return clearCloudContext(
          state,
          "refused",
          "A callback result arrived outside its active verification step.",
          consumeCallback(state, event.callbackId)
        );
      }
      return {
        ...state,
        status: "authenticated-no-organization",
        attempt: null,
        activeCallbackId: null,
        consumedCallbackIds: consumeCallback(state, event.callbackId),
        identity: event.identity,
        organizations: event.organizations,
        organization: null,
        project: null,
        message: "Choose an organization to continue."
      };
    case "select-organization": {
      const organization = state.organizations.find((candidate) => candidate.id === event.organizationId);
      if (state.identity === null || organization === undefined) {
        return clearCloudContext(state, "refused", "The selected organization is not available in this session.");
      }
      return {
        ...state,
        status: "authenticated-no-project",
        organization,
        project: null,
        message: "Choose a read-only project context."
      };
    }
    case "select-project": {
      const project = state.organization?.projects.find((candidate) => candidate.id === event.projectId);
      if (state.identity === null || state.organization === null || project === undefined) {
        return clearCloudContext(state, "refused", "The selected project is not available in this organization.");
      }
      return {
        ...state,
        status: "project-readonly",
        project,
        message: "Read-only cloud context is ready. Local files remain unattached."
      };
    }
    case "expired":
      return clearCloudContext(state, "expired", event.message);
    case "refused":
      return clearCloudContext(
        state,
        "refused",
        event.message,
        event.callbackId === undefined ? state.consumedCallbackIds : consumeCallback(state, event.callbackId)
      );
    case "unavailable":
      return clearCloudContext(state, "unavailable", event.message);
    case "cancel":
      return clearCloudContext(
        state,
        "anonymous-local",
        "Sign-in was cancelled. Local files and pending changes were kept."
      );
    case "sign-out":
      return {
        ...clearCloudContext(
          state,
          "anonymous-local",
          "Signed out of the cloud context. Local files and pending changes were kept."
        ),
        returnTo: null
      };
  }
}

export function parseAuthReturnTo(raw: string): AuthReturnTarget | null {
  if (!raw.startsWith("/workspace?") || raw.startsWith("//") || raw.includes("%") || raw.includes("#")) return null;
  const url = new URL(raw, "https://workbench.invalid");
  if (url.origin !== "https://workbench.invalid" || url.pathname !== "/workspace") return null;
  if ([...url.searchParams.keys()].join(",") !== "tool") return null;
  const tool = url.searchParams.get("tool");
  if (tool !== "inspect" && tool !== "codes" && tool !== "components") return null;
  return { route: "/workspace", tool };
}

export function authReturnTo(tool: AuthToolId): string {
  return `/workspace?tool=${tool}`;
}
